import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Clock, Users, Volume2, Shield, Zap, Timer, Info } from "lucide-react";

const noiseIcons = { low: "text-green-600", medium: "text-amber-500", high: "text-red-500" };
const safetyLabels = { low: "Casual", medium: "Standard", high: "Competitive" };

export default function ActivityDetailModal({ open, onOpenChange, activity }) {
  if (!activity) return null;

  const sportName = activity.activity_name || activity.sport_type || activity.product_name || "Activity";
  const spaceName = activity.space_name || activity.court_name || "";
  const partition = activity.partition_name || activity.marketing_tags_json?.partition || "";
  const bookingMode = activity.booking_mode || activity.product_type || "";
  const noise = activity.noise_class || "medium";
  const safety = activity.safety_class || "medium";
  const setupMin = activity.setup_buffer_minutes || 0;
  const teardownMin = activity.teardown_buffer_minutes || 0;
  const minDuration = activity.min_duration_minutes || 60;
  const maxDuration = activity.max_duration_minutes || 180;
  const maxParticipants = activity.max_participants || activity.capacity;
  const price = activity.display_from_price_amount || activity.base_rate || activity.rate;
  const peakAllowed = activity.allowed_peak !== false;
  const nonPeakAllowed = activity.allowed_non_peak !== false;
  const needsApproval = activity.requires_manual_approval === true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="activity-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight" data-testid="activity-detail-title">
            {sportName}
          </DialogTitle>
          {spaceName && (
            <p className="text-sm text-muted-foreground mt-1">{spaceName}{partition ? ` \u00b7 ${partition}` : ""}</p>
          )}
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Price */}
          {price && (
            <div className="bg-primary/5 border border-primary/20 rounded-sm p-3 flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Starting Rate</span>
              <span className="font-heading text-xl font-bold text-primary" data-testid="activity-detail-price">{"\u20B1"}{Number(price).toLocaleString()}/hr</span>
            </div>
          )}

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-3">
            {maxParticipants && (
              <div className="flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-muted-foreground text-xs">Max Players</p>
                  <p className="font-medium" data-testid="activity-detail-capacity">{maxParticipants}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-muted-foreground text-xs">Duration</p>
                <p className="font-medium">{minDuration}–{maxDuration} min</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Volume2 className={`w-4 h-4 shrink-0 ${noiseIcons[noise] || "text-muted-foreground"}`} />
              <div>
                <p className="text-muted-foreground text-xs">Noise Level</p>
                <p className="font-medium capitalize" data-testid="activity-detail-noise">{noise}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Shield className={`w-4 h-4 shrink-0 ${noise === "high" ? "text-red-500" : "text-muted-foreground"}`} />
              <div>
                <p className="text-muted-foreground text-xs">Intensity</p>
                <p className="font-medium">{safetyLabels[safety] || "Standard"}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Setup & Teardown */}
          {(setupMin > 0 || teardownMin > 0) && (
            <div className="flex items-center gap-2 text-sm">
              <Timer className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Setup/Teardown:</span>
              <span className="font-medium">{setupMin > 0 ? `${setupMin}min setup` : ""}{setupMin > 0 && teardownMin > 0 ? " + " : ""}{teardownMin > 0 ? `${teardownMin}min teardown` : ""}</span>
            </div>
          )}

          {/* Booking Mode & Availability */}
          <div className="flex flex-wrap gap-2">
            {bookingMode && (
              <Badge variant="outline" className="text-xs capitalize" data-testid="activity-detail-mode">
                <Zap className="w-3 h-3 mr-1" />
                {bookingMode === "instant" || bookingMode === "instant_book" ? "Instant Book" : bookingMode.replace(/_/g, " ")}
              </Badge>
            )}
            {needsApproval && <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Requires Approval</Badge>}
            {peakAllowed && <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">Peak Hours</Badge>}
            {nonPeakAllowed && <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">Off-Peak</Badge>}
          </div>

          {/* Features from marketing tags */}
          {activity.marketing_tags_json && Object.keys(activity.marketing_tags_json).length > 0 && (
            <div>
              <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">Features</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(activity.marketing_tags_json).map(([key, val]) => (
                  <Badge key={key} variant="secondary" className="text-xs">
                    <Info className="w-3 h-3 mr-1" />{String(val)}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
